/**
 * 進捗ストア（src/lib/progress の createStore）を画面から読むための薄い包み。
 * ストアは素の値を返すので、書き込みのたびに snapshot を $state.raw に取り直して画面を更新する。
 * ロジック（箱の遷移・キュー・ストリーク・取り込み）はすべてストアと src/lib/progress が持つ。
 */
import {
  DRAFT_TOO_LONG_MESSAGE,
  NEWER_VERSION_MESSAGE,
  SAVE_FAILED_MESSAGE,
  toLocalDate,
  type AnswerInput,
  type LessonRequirement,
  type LessonState,
  type LessonStep,
  type ExportFile,
  type LocalDate,
  type ParseImportResult,
  type ProgressStore,
  type StoreNotice,
  type StoreSnapshot,
} from '../../lib/progress';

export const CORRUPT_MESSAGE =
  '保存されていた記録を読めなかったため、新しく始めました。元のデータはこの端末に退避しています。';

/** ストアの知らせ（progress-store.md §1.1, §6.3）を画面の文言にする。 */
export function noticeMessage(n: StoreNotice): { message: string; kind: 'warn' | 'err' } {
  switch (n.kind) {
    case 'save-failed':
      return { message: `${SAVE_FAILED_MESSAGE}。`, kind: 'err' };
    case 'newer-version':
      return {
        message: `${NEWER_VERSION_MESSAGE}。この画面では読み込みも保存もしません。`,
        kind: 'warn',
      };
    case 'corrupt':
      return { message: CORRUPT_MESSAGE, kind: 'warn' };
    case 'draft-too-long':
      return { message: `${DRAFT_TOO_LONG_MESSAGE}。`, kind: 'warn' };
  }
}

export function createProgressView(store: ProgressStore, now: () => Date) {
  let snap = $state.raw<StoreSnapshot>(store.snapshot());
  let streak = $state(store.streak());
  let backup = $state(store.hasBackup());

  function refresh<T>(value: T): T {
    snap = store.snapshot();
    streak = store.streak();
    backup = store.hasBackup();
    return value;
  }

  return {
    get snapshot(): StoreSnapshot {
      return snap;
    },
    get streak(): number {
      return streak;
    },
    get hasBackup(): boolean {
      return backup;
    },
    today(): LocalDate {
      return toLocalDate(now());
    },
    now,
    load: (): StoreNotice[] => refresh(store.load()),
    handleStorageEvent: (key: string | null): StoreNotice[] =>
      refresh(store.handleStorageEvent(key)),
    recordAnswer: (input: AnswerInput) => refresh(store.recordAnswer(input)),
    /** レッスンの出来事（progress-store.md §7）。requirement を渡すと完了日を付ける */
    markLessonStep: (lessonId: string, step: LessonStep, requirement?: LessonRequirement) =>
      refresh(store.markLessonStep(lessonId, step, requirement)),
    /** snapshot を読むので、画面の $derived から呼ぶと記録のたびに更新される */
    lessonState: (lessonId: string, requirement?: LessonRequirement): LessonState => {
      void snap;
      return store.lessonState(lessonId, requirement);
    },
    exportFile: (): { file: ExportFile; notices: StoreNotice[] } => refresh(store.exportFile()),
    importText: (text: string): ParseImportResult & { notices: StoreNotice[] } =>
      refresh(store.importText(text)),
    undoImport: (): (ParseImportResult & { notices: StoreNotice[] }) | null =>
      refresh(store.undoImport()),
  };
}

export type ProgressView = ReturnType<typeof createProgressView>;
